"use client"

import { useEffect, useMemo, useState } from "react"
import { useTranslations } from "next-intl"
import { Check, Clock, Loader2, Undo2, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useOffer, useOfferActions } from "@/hooks/use-offer"
import { useToast } from "@/hooks/use-toast"
import { OfferModal } from "./offer-modal"
import { currencySymbol, isPendingStatus } from "@/lib/offers/offer-utils"
import { offerCountdownShort } from "@/lib/offers/offer-hub-filters"
import { cn, formatPrice } from "@/lib/utils"
import type { OfferStatus } from "@/lib/offers/offer-types"

interface OfferBubblePayload {
  type: "offer"
  id: string
  amount: number
  buyer_id: string
  seller_id: string
  listing_id: string
  expires_at: string
  message?: string
  status?: OfferStatus
  currency?: string
  listing_price?: number | null
  parent_offer_id?: string
  chain_depth?: number
}

interface OfferBubbleProps {
  payload: OfferBubblePayload
  currentUserId: string | null
  /** When the chat layout is "own message" we mirror styling to the right side. */
  ownMessage?: boolean
}

export function OfferBubble({ payload, currentUserId, ownMessage }: OfferBubbleProps) {
  const t = useTranslations()
  const { toast } = useToast()
  const actions = useOfferActions()
  const [counterOpen, setCounterOpen] = useState(false)
  const [pending, setPending] = useState<null | "accept" | "decline" | "withdraw">(null)
  const [error, setError] = useState<string | null>(null)

  // Keep the realtime slot fresh for this offer
  const slot = useOffer(payload.id, {
    initial: {
      amount: payload.amount,
      status: payload.status ?? "pending",
      expiresAt: payload.expires_at,
      paymentDeadline: null,
      settledAt: null,
      eventVersion: 0,
    },
  })

  const status = slot?.status ?? payload.status ?? "pending"
  const amount = slot?.amount ?? payload.amount
  const expiresAt = slot?.expiresAt ?? payload.expires_at
  const paymentDeadline = slot?.paymentDeadline ?? null
  const settledAt = slot?.settledAt ?? null
  const symbol = currencySymbol(payload.currency ?? null)

  const isSeller = currentUserId != null && currentUserId === payload.seller_id
  const isBuyer = currentUserId != null && currentUserId === payload.buyer_id
  const chainDepth = payload.chain_depth ?? (payload.parent_offer_id ? 1 : 0)
  const isAcceptedAwaitingPayment = status === "accepted" && settledAt == null

  const countdown = useCountdown(expiresAt, status)
  const paymentCountdownLabel =
    isAcceptedAwaitingPayment && paymentDeadline
      ? offerCountdownShort(paymentDeadline)
      : null
  const isExpired = countdown == null && isPendingStatus(status)

  const sellerCanRespond =
    isSeller && isPendingStatus(status) && !isExpired && chainDepth % 2 === 0
  const buyerCanRespond =
    isBuyer && isPendingStatus(status) && !isExpired && chainDepth % 2 === 1

  const handleAccept = async () => {
    if (!sellerCanRespond && !buyerCanRespond) return
    setPending("accept")
    setError(null)
    const res = await actions.acceptOffer(payload.id)
    if (!res.ok) {
      setError(extractError(res.data, t))
    } else {
      toast({
        title: t("offers.toasts.accepted.title", { default: "Offer accepted" }),
        description: isBuyer
          ? t("offers.toasts.acceptedBuyer.description", {
              default: "Complete payment before the deadline.",
            })
          : t("offers.toasts.accepted.description", {
              default: "The buyer can now complete payment.",
            }),
      })
    }
    setPending(null)
  }

  const handleDecline = async () => {
    if (!sellerCanRespond && !buyerCanRespond) return
    setPending("decline")
    setError(null)
    const res = await actions.declineOffer(payload.id)
    if (!res.ok) setError(extractError(res.data, t))
    setPending(null)
  }

  const handleWithdraw = async () => {
    if (!isBuyer) return
    setPending("withdraw")
    setError(null)
    const res = await actions.withdrawOffer(payload.id)
    if (!res.ok) setError(extractError(res.data, t))
    setPending(null)
  }

  const showActions = isPendingStatus(status) && !isExpired && (isSeller || isBuyer)
  const showAcceptDecline = sellerCanRespond || buyerCanRespond

  return (
    <>
      <div
        className={cn(
          "rounded-2xl border p-3.5 max-w-[320px] flex flex-col gap-2.5",
          ownMessage
            ? "bg-primary/10 border-primary/30 ml-auto"
            : "bg-muted border-border",
          status === "accepted" && "ring-1 ring-emerald-500/50",
          status === "declined" && "opacity-70",
          isExpired && "opacity-60"
        )}
      >
        <div className="flex items-baseline justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              {t("offers.bubble.title", { default: "Offer" })}
            </p>
            <p className="text-2xl font-bold font-mono">
              {symbol}
              {amount}
            </p>
          </div>
          <StatusBadge status={isExpired ? "expired" : status} />
        </div>

        {payload.message && (
          <p className="text-sm text-muted-foreground italic">"{payload.message}"</p>
        )}

        {isPendingStatus(status) && !isExpired && countdown && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock className="h-3.5 w-3.5" />
            <span>
              {t("offers.bubble.expiresIn", { default: "Expires in" })} {countdown}
            </span>
          </div>
        )}

        {isAcceptedAwaitingPayment && paymentCountdownLabel ? (
          <div className="flex items-center justify-between gap-2 rounded-lg bg-emerald-500/10 px-2.5 py-2 text-xs">
            <div className="flex items-center gap-1.5 text-emerald-600">
              <Clock className="h-3.5 w-3.5" />
              <span>
                {isBuyer
                  ? t("offers.card.reservedForYou", { default: "Reserved for you" })
                  : t("offers.card.awaitingPayment", { default: "Awaiting payment" })}
              </span>
            </div>
            <span className="font-semibold text-emerald-600">{paymentCountdownLabel}</span>
          </div>
        ) : null}

        {error && (
          <p className="text-xs text-destructive">{error}</p>
        )}

        {showActions && (
          <div className="flex flex-col gap-1.5 pt-1">
            {showAcceptDecline && (
              <div className="flex gap-1.5">
                <Button
                  size="sm"
                  className="flex-1"
                  onClick={handleAccept}
                  disabled={pending !== null}
                >
                  {pending === "accept" ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4 mr-1" />
                  )}
                  {t("offers.bubble.accept", { default: "Accept" })}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1"
                  onClick={handleDecline}
                  disabled={pending !== null}
                >
                  {pending === "decline" ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <X className="h-4 w-4 mr-1" />
                  )}
                  {t("offers.bubble.decline", { default: "Decline" })}
                </Button>
              </div>
            )}
            <div className="flex gap-1.5">
              {showAcceptDecline && (
                <Button
                  size="sm"
                  variant="secondary"
                  className="flex-1"
                  onClick={() => setCounterOpen(true)}
                  disabled={pending !== null}
                >
                  <Undo2 className="h-4 w-4 mr-1" />
                  {t("offers.bubble.counter", { default: "Counter" })}
                </Button>
              )}
              {isBuyer && isPendingStatus(status) && !isExpired && (
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1"
                  onClick={handleWithdraw}
                  disabled={pending !== null}
                >
                  {pending === "withdraw" ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <X className="h-4 w-4 mr-1" />
                  )}
                  {t("offers.bubble.withdraw", { default: "Withdraw" })}
                </Button>
              )}
            </div>
          </div>
        )}

        {isAcceptedAwaitingPayment && isBuyer && (
          <Button
            size="sm"
            className="w-full"
            onClick={() => {
              const evt = new CustomEvent("open-offer-checkout", { detail: { offerId: payload.id } })
              window.dispatchEvent(evt)
            }}
          >
            {t("offers.bubble.payNow", { default: "Pay now" })}{" "}
            {payload.listing_price != null
              ? formatPrice(String(amount), payload.currency ?? undefined)
              : `${symbol}${amount}`}
          </Button>
        )}
      </div>

      <OfferModal
        open={counterOpen}
        onOpenChange={setCounterOpen}
        mode={{
          kind: "counter",
          parentOfferId: payload.id,
          listingPrice: payload.listing_price ?? null,
          currency: payload.currency ?? null,
          receivedOfferAmount: payload.amount,
        }}
      />
    </>
  )
}

function StatusBadge({ status }: { status: OfferStatus | "expired" }) {
  const t = useTranslations()
  const cls = useMemo(() => {
    switch (status) {
      case "accepted":
        return "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
      case "declined":
        return "bg-red-500/15 text-red-400 border-red-500/30"
      case "counter_offered":
        return "bg-amber-500/15 text-amber-400 border-amber-500/30"
      case "expired":
      case "withdrawn":
        return "bg-neutral-500/15 text-neutral-400 border-neutral-500/30"
      default:
        return "bg-blue-500/15 text-blue-400 border-blue-500/30"
    }
  }, [status])

  const label = useMemo(() => {
    switch (status) {
      case "accepted":
        return t("offers.status.accepted", { default: "Accepted" })
      case "declined":
        return t("offers.status.declined", { default: "Declined" })
      case "counter_offered":
        return t("offers.status.countered", { default: "Countered" })
      case "expired":
        return t("offers.status.expired", { default: "Expired" })
      case "withdrawn":
        return t("offers.status.withdrawn", { default: "Withdrawn" })
      default:
        return t("offers.status.pending", { default: "Pending" })
    }
  }, [status, t])

  return (
    <span className={cn("text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border", cls)}>
      {label}
    </span>
  )
}

function useCountdown(expiresAt: string, status: OfferStatus): string | null {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!isPendingStatus(status)) return
    const id = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(id)
  }, [status])

  if (!isPendingStatus(status)) return null
  const target = new Date(expiresAt).getTime()
  if (!Number.isFinite(target)) return null
  const diff = target - now
  if (diff <= 0) return null

  const hours = Math.floor(diff / (3_600_000))
  const minutes = Math.floor((diff % 3_600_000) / 60_000)
  if (hours >= 24) {
    const days = Math.floor(hours / 24)
    return `${days}d ${hours % 24}h`
  }
  if (hours > 0) return `${hours}h ${minutes}m`
  return `${Math.max(1, minutes)}m`
}

function extractError(data: unknown, t: ReturnType<typeof useTranslations>): string {
  const obj = data as { reason?: string; error?: string } | null
  const reason = obj?.reason ?? obj?.error ?? "unknown"
  switch (reason) {
    case "forbidden":
      return t("offers.errors.forbidden", { default: "You can't perform this action." })
    case "invalid_state":
      return t("offers.errors.invalidState", { default: "This offer is no longer pending." })
    case "offer_expired":
    case "parent_expired":
      return t("offers.errors.expired", { default: "This offer has expired." })
    default:
      return t("offers.errors.generic", { default: "Something went wrong. Please try again." })
  }
}
